// Auth token storage for the Capacitor build only.
//
// On the web the session is an httpOnly SameSite=strict cookie and nothing
// here ever runs -- that cookie is invisible to JavaScript by design, which
// is what stops an XSS from stealing a session, and none of that changes.
//
// The app can't use that cookie. Its WebView is served from
// capacitor://localhost (iOS) or https://localhost (Android), a different
// *site* from the API domain, so a strict cookie is never attached to its
// requests: login would look like it worked and every call after it would go
// out anonymous. So on native only, the server also returns the token in the
// login response (see NATIVE_APP_ORIGINS in backend/routes/auth.js) and we
// send it back as `Authorization: Bearer`, which the auth middleware already
// accepts as a fallback.
//
// STORAGE TRADEOFF: this uses localStorage, which inside a native WebView is
// sandboxed to the app -- another app cannot read it, unlike a shared cookie
// jar. It is still not the Keychain/Keystore. Before shipping to the stores
// this should move to @capacitor/preferences backed by secure storage; the
// read/write calls are already funnelled through this module so that change
// lands in one place.

const STORAGE_KEY = 'acymart_native_token';

// Capacitor injects window.Capacitor into the WebView. Checked lazily rather
// than at module load because the plugin bootstraps before React mounts but
// not necessarily before this module is first imported.
export function isNativeApp() {
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
  if (!cap) return false;
  return typeof cap.isNativePlatform === 'function'
    ? cap.isNativePlatform()
    : Boolean(cap.isNative);
}

let cached = null;

export function getSessionToken() {
  if (!isNativeApp()) return null;
  if (cached) return cached;
  try {
    cached = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    cached = null; // private mode / storage disabled -- fall through to no token
  }
  return cached;
}

// Called after any response that may carry a token (login, 2FA, OTP verify,
// register). A web response never contains one, so this is a no-op there.
export function rememberSessionToken(token) {
  if (!isNativeApp() || !token) return;
  cached = token;
  try {
    window.localStorage.setItem(STORAGE_KEY, token);
  } catch {
    /* keep the in-memory copy; the session simply won't survive a restart */
  }
}

export function clearSessionToken() {
  cached = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing durable to clear */
  }
}
